import { useEffect, useState } from 'react';
import {
  MAILERLITE_DEFAULT_GROUP_NAME,
  MAILERLITE_STATUS_LABELS,
  MAILERLITE_SUBSCRIBER_STATUSES,
  emptyMailerliteFilter,
  type MailerliteContactPreview,
  type MailerliteFilterGroup,
  type MailerliteStatusRefreshResult,
  type MailerliteSyncResult,
} from '@catalyst-psa/shared';

import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { DataState } from '../../components/ui/DataState.js';
import { CheckboxField, SelectField, TextField } from '../../components/ui/Field.js';
import { MailerliteFilterBuilder } from './MailerliteFilterBuilder.js';
import { MailerliteRunLog } from './MailerliteRunLog.js';
import {
  useMailerliteSettings,
  usePreviewMailerlite,
  useRefreshMailerliteStatuses,
  useRunMailerlite,
  useTestMailerlite,
  useUpdateMailerliteSettings,
} from './api.js';

const INTERVAL_OPTIONS = [
  { value: '60', label: 'Every hour' },
  { value: '360', label: 'Every 6 hours' },
  { value: '720', label: 'Every 12 hours' },
  { value: '1440', label: 'Daily' },
  { value: '10080', label: 'Weekly' },
];

export function MailerlitePage(): JSX.Element {
  const settingsQuery = useMailerliteSettings();
  const updateMutation = useUpdateMailerliteSettings();
  const testMutation = useTestMailerlite();
  const previewMutation = usePreviewMailerlite();
  const runMutation = useRunMailerlite();
  const statusMutation = useRefreshMailerliteStatuses();

  const settings = settingsQuery.data;

  const [enabled, setEnabled] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [groupName, setGroupName] = useState(MAILERLITE_DEFAULT_GROUP_NAME);
  const [filter, setFilter] = useState<MailerliteFilterGroup>(emptyMailerliteFilter());
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState('1440');
  const [selected, setSelected] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);

  // Seed the form from the server once the settings land.
  useEffect(() => {
    if (!settings) return;
    setEnabled(settings.enabled);
    setGroupName(settings.groupName);
    setFilter(settings.filter);
    setScheduleEnabled(settings.schedule.enabled);
    setIntervalMinutes(String(settings.schedule.intervalMinutes ?? 1440));
  }, [settings]);

  const handleSave = (): void => {
    setSaved(false);
    updateMutation.mutate(
      {
        enabled,
        groupName,
        filter,
        scheduleEnabled,
        intervalMinutes: Number(intervalMinutes),
        ...(apiKey ? { apiKey } : {}),
      },
      {
        onSuccess: () => {
          setApiKey('');
          setSaved(true);
        },
      },
    );
  };

  const preview = previewMutation.data;
  const run = runMutation.data;
  const statuses = statusMutation.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="MailerLite"
        description="Copy PSA contacts into a MailerLite group. Each contact is sent once."
      />

      <DataState
        isLoading={settingsQuery.isLoading}
        isError={settingsQuery.isError}
        isEmpty={false}
        onRetry={() => void settingsQuery.refetch()}
      >
        <div className="space-y-6">
          <Card title="Connection">
            <div className="space-y-4">
              <CheckboxField
                label="Enable the MailerLite integration"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <TextField
                label="API key"
                type="password"
                autoComplete="off"
                placeholder={settings?.hasApiKey ? '•••••••• (stored)' : 'Paste your MailerLite API key'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
              {settings && !settings.canStoreSecrets ? (
                <p className="text-sm text-amber-700">
                  This server has no credential encryption key, so an API key cannot be saved from
                  the UI. Set MAILERLITE_CONFIG_ENCRYPTION_KEY.
                </p>
              ) : null}
              <TextField
                label="Group name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              />
              <p className="-mt-2 text-xs text-slate-500">
                Contacts are added to this MailerLite group. It is created automatically if it does
                not exist yet.
                {settings?.groupId ? ` Resolved group id: ${settings.groupId}.` : ''}
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <Button onClick={handleSave} disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Saving…' : 'Save settings'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => testMutation.mutate()}
                  disabled={testMutation.isPending}
                >
                  {testMutation.isPending ? 'Testing…' : 'Test connection'}
                </Button>
                {saved ? <Badge tone="green">Saved</Badge> : null}
                {updateMutation.isError ? (
                  <span className="text-sm text-red-600">
                    {(updateMutation.error as Error)?.message ?? 'Could not save.'}
                  </span>
                ) : null}
              </div>

              {testMutation.data ? (
                <div
                  className={`rounded border p-3 text-sm ${
                    testMutation.data.ok
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                      : 'border-red-200 bg-red-50 text-red-800'
                  }`}
                >
                  <p>{testMutation.data.message}</p>
                  {testMutation.data.groups?.length ? (
                    <p className="mt-1 text-xs">Groups: {testMutation.data.groups.join(', ')}</p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </Card>

          <Card title="Which contacts qualify">
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Build any combination of rules. Groups nest, so you can express things like
                “send to MailerLite is checked AND (billing contact is checked OR project contact
                is checked)”. An empty filter matches every contact.
              </p>
              <MailerliteFilterBuilder value={filter} onChange={setFilter} />
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="secondary"
                  onClick={() => previewMutation.mutate({ filter })}
                  disabled={previewMutation.isPending}
                >
                  {previewMutation.isPending ? 'Checking…' : 'Preview matching contacts'}
                </Button>
                <Button onClick={handleSave} disabled={updateMutation.isPending}>
                  Save filter
                </Button>
                {previewMutation.isError ? (
                  <span className="text-sm text-red-600">
                    {(previewMutation.error as Error)?.message ?? 'Filter is not valid.'}
                  </span>
                ) : null}
              </div>

              {preview ? <PreviewPanel preview={preview} selected={selected} onSelect={setSelected} /> : null}
            </div>
          </Card>

          <Card title="Send">
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="secondary"
                  onClick={() => runMutation.mutate({ dryRun: true })}
                  disabled={runMutation.isPending}
                >
                  Dry run (nothing sent)
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => runMutation.mutate({ contactIds: selected })}
                  disabled={runMutation.isPending || selected.length === 0}
                >
                  Send {selected.length} selected
                </Button>
                <Button
                  onClick={() => runMutation.mutate({})}
                  disabled={runMutation.isPending || !settings?.enabled}
                >
                  {runMutation.isPending ? 'Sending…' : 'Send all matching now'}
                </Button>
                {runMutation.isError ? (
                  <span className="text-sm text-red-600">
                    {(runMutation.error as Error)?.message ?? 'The run failed.'}
                  </span>
                ) : null}
              </div>
              {!settings?.enabled ? (
                <p className="text-sm text-amber-700">
                  Enable the integration above before sending.
                </p>
              ) : null}
              {run ? <RunPanel result={run} /> : null}
            </div>
          </Card>

          <Card title="Subscriber status">
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Reads each sent contact back from MailerLite so unsubscribes, bounces and spam
                complaints show up on the contact record. This also runs on the schedule below.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="secondary"
                  onClick={() => statusMutation.mutate()}
                  disabled={statusMutation.isPending || !settings?.enabled}
                >
                  {statusMutation.isPending ? 'Checking…' : 'Refresh statuses now'}
                </Button>
                {statusMutation.isError ? (
                  <span className="text-sm text-red-600">
                    {(statusMutation.error as Error)?.message ?? 'The status refresh failed.'}
                  </span>
                ) : null}
              </div>
              {statuses ? <StatusPanel result={statuses} /> : null}
            </div>
          </Card>

          <Card title="Schedule">
            <div className="space-y-4">
              <CheckboxField
                label="Push new matching contacts automatically"
                checked={scheduleEnabled}
                onChange={(e) => setScheduleEnabled(e.target.checked)}
              />
              <SelectField
                label="How often"
                value={intervalMinutes}
                disabled={!scheduleEnabled}
                options={INTERVAL_OPTIONS}
                onChange={(e) => setIntervalMinutes(e.target.value)}
              />
              <Button onClick={handleSave} disabled={updateMutation.isPending}>
                Save schedule
              </Button>
              {settings ? <LastRun schedule={settings.schedule} /> : null}
            </div>
          </Card>

          <MailerliteRunLog />
        </div>
      </DataState>
    </div>
  );
}

/** Summary of the last subscriber-status refresh. */
function StatusPanel({ result }: { result: MailerliteStatusRefreshResult }): JSX.Element {
  const tone = result.outcome === 'ok' ? 'green' : result.outcome === 'partial' ? 'amber' : 'red';
  return (
    <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={tone}>{result.outcome}</Badge>
        <span className="text-slate-600">
          {result.checked} checked · {result.changed} changed · {result.failed} failed
        </span>
      </div>
      {result.checked > 0 ? (
        <p className="mt-1 text-slate-600">
          {MAILERLITE_SUBSCRIBER_STATUSES.filter((s) => result.byStatus[s] > 0)
            .map((s) => `${result.byStatus[s]} ${MAILERLITE_STATUS_LABELS[s].toLowerCase()}`)
            .join(' · ') || 'No statuses reported.'}
        </p>
      ) : null}
      {result.message ? <p className="mt-1 text-slate-500">{result.message}</p> : null}
    </div>
  );
}

function LastRun({  schedule,
}: {
  schedule: NonNullable<ReturnType<typeof useMailerliteSettings>['data']>['schedule'];
}): JSX.Element {
  if (!schedule.lastRunAt) {
    return <p className="text-sm text-slate-500">This integration has not run yet.</p>;
  }
  const tone = schedule.lastOutcome === 'ok' ? 'green' : schedule.lastOutcome === 'partial' ? 'amber' : 'red';
  return (
    <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={tone}>{schedule.lastOutcome ?? 'unknown'}</Badge>
        <span className="text-slate-600">
          Last run {new Date(schedule.lastRunAt).toLocaleString()}
        </span>
        {schedule.nextRunAt ? (
          <span className="text-slate-500">
            · next {new Date(schedule.nextRunAt).toLocaleString()}
          </span>
        ) : null}
      </div>
      <p className="mt-1 text-slate-600">
        {schedule.lastMatched} matched · {schedule.lastPushed} sent · {schedule.lastSkipped} skipped
        · {schedule.lastFailed} failed
      </p>
      {schedule.lastMessage ? (
        <p className="mt-1 text-slate-500">{schedule.lastMessage}</p>
      ) : null}
    </div>
  );
}

function PreviewPanel({
  preview,
  selected,
  onSelect,
}: {
  preview: { total: number; eligible: number; alreadyPushed: number; contacts: MailerliteContactPreview[] };
  selected: string[];
  onSelect: (ids: string[]) => void;
}): JSX.Element {
  const toggle = (id: string): void => {
    onSelect(selected.includes(id) ? selected.filter((v) => v !== id) : [...selected, id]);
  };
  return (
    <div className="rounded border border-slate-200">
      <div className="flex flex-wrap gap-4 border-b border-slate-200 bg-slate-50 px-3 py-2 text-sm">
        <span>
          <strong>{preview.total}</strong> matched
        </span>
        <span className="text-emerald-700">
          <strong>{preview.eligible}</strong> ready to send
        </span>
        <span className="text-slate-500">
          <strong>{preview.alreadyPushed}</strong> already sent
        </span>
        <span className="ml-auto text-xs text-slate-400">
          Showing the first {preview.contacts.length}. Tick rows to send just those.
        </span>
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="w-8 px-3 py-2" />
            <th className="px-3 py-2">Contact</th>
            <th className="px-3 py-2">Email</th>
            <th className="px-3 py-2">Customer</th>
            <th className="px-3 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {preview.contacts.map((contact) => (
            <tr key={contact.contactId} className="border-t border-slate-100">
              <td className="px-3 py-2">
                <input
                  type="checkbox"
                  checked={selected.includes(contact.contactId)}
                  disabled={!contact.eligible}
                  onChange={() => toggle(contact.contactId)}
                />
              </td>
              <td className="px-3 py-2 text-slate-800">{contact.name}</td>
              <td className="px-3 py-2 text-slate-600">{contact.email ?? '—'}</td>
              <td className="px-3 py-2 text-slate-600">{contact.customerName}</td>
              <td className="px-3 py-2">
                {contact.alreadyPushed ? (
                  <Badge tone="gray">Already sent</Badge>
                ) : contact.eligible ? (
                  <Badge tone="green">Ready</Badge>
                ) : (
                  <Badge tone="amber">{contact.reason ?? 'Not eligible'}</Badge>
                )}
              </td>
            </tr>
          ))}
          {preview.contacts.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                No contacts match this filter.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function RunPanel({ result }: { result: MailerliteSyncResult }): JSX.Element {
  const tone = result.outcome === 'ok' ? 'green' : result.outcome === 'partial' ? 'amber' : 'red';
  return (
    <div className="rounded border border-slate-200">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2 text-sm">
        <Badge tone={tone}>{result.outcome}</Badge>
        {result.dryRun ? <Badge tone="blue">Dry run</Badge> : null}
        {result.scoped ? <Badge tone="blue">Selected only</Badge> : null}
        <span className="text-slate-600">
          {result.matched} matched · {result.pushed} sent · {result.alreadyPushed} already ·{' '}
          {result.skipped} skipped · {result.failed} failed
          {result.deferred > 0 ? ` · ${result.deferred} deferred` : ''}
        </span>
        {result.message ? <span className="text-slate-500">{result.message}</span> : null}
      </div>
      <ul className="max-h-72 divide-y divide-slate-100 overflow-auto text-sm">
        {result.changes.map((change) => (
          <li key={change.contactId} className="flex flex-wrap items-center gap-2 px-3 py-1.5">
            <Badge
              tone={
                change.status === 'pushed'
                  ? 'green'
                  : change.status === 'failed'
                    ? 'red'
                    : change.status === 'skipped'
                      ? 'amber'
                      : 'gray'
              }
            >
              {change.status}
            </Badge>
            <span className="text-slate-800">{change.name}</span>
            <span className="text-slate-500">{change.email ?? '—'}</span>
            <span className="text-slate-400">{change.customerName}</span>
            {change.detail ? (
              <span className="ml-auto text-xs text-slate-500">{change.detail}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
